/**
 * Reframe API client for the XQ Competency Navigator
 *
 * This module provides functions to interact with the Reframe API,
 * replacing the previous Firebase function-based search.
 *
 * Note: API calls are made through a local proxy route (/api/reframe/search)
 * to keep API credentials server-side.
 */

export type SearchResult = {
  id: string;
  name: string;
  level: number;
  description: string;
  content: string;
  score: number;
};

/**
 * Search competencies using the Reframe API (via local proxy)
 *
 * @param text - The search query text
 * @returns Promise resolving to an array of search results
 * @throws Error if the API request fails
 */
export async function searchCompetencies(
  text: string,
): Promise<SearchResult[]> {
  // Call local proxy route to keep API credentials server-side
  const url = new URL("/api/reframe/search", window.location.origin);
  url.searchParams.set("text", text);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message ||
        `Search request failed with status ${response.status}`,
    );
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.message || "Search failed");
  }

  return data.results;
}

// Note: The createDebouncedSearch helper has been removed.
// Components should use searchCompetencies directly with their own debounce logic.
